/**
 * useAnalyticsData – Shared hook for Analytics Dashboard
 *
 * Consolidates ALL data fetching (RTK Query only – no manual API calls),
 * date-range computation, and data transformations that are shared across tabs.
 *
 * Benefits:
 * - Single source of truth for computed date range
 * - RTK Query caching eliminates duplicate network requests
 * - useMemo-derived data used directly – no useState/useEffect sync overhead
 * - Tab-specific queries use `skip` so they only fire when needed
 */

import { useMemo } from 'react';
import {
  useGetROSummaryQuery,
  useGetTopPerformingSummaryQuery,
  useGetAttendancePercDateRangeQuery,
  useGetDailyAttendanceSummaryQuery,
  useGetTotalKVISQuery,
  useGetTotalEmployeesQuery,
  useGetStaffPresentTodayQuery,
  useGetStaffAbsentTodayQuery,
  useGetDailyAttendanceByKVSNoPagingQuery,
} from '../../services/attendanceApi';

import type {
  RegionTrendData,
  RankingEntry,
  TimeSeriesPoint,
} from '../../types/analytics';

// ─── Color palette for regions ───────────────────────────────
export const REGION_COLORS = [
  '#6B1786', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6', '#a855f7',
];

// ─── Pure helpers (no hooks) ─────────────────────────────────

/** Convert region trend objects to ranking table entries */
export const regionTrendsToRankings = (trends: RegionTrendData[]): RankingEntry[] =>
  trends.map(t => ({
    rank: t.rank,
    previousRank: (t as any).previousRank,
    rankChange: ((t as any).previousRank || t.rank) - t.rank,
    id: t.roId,
    name: t.roName,
    type: 'region' as const,
    attendancePercent: Math.round(t.averageAttendance * 10) / 10,
    trendDirection: t.trendDirection,
    trendPercent: t.trendPercent,
  }));

/** Convert API employee performance data to ranking table entries */
export const apiEmployeesToRankings = (employees: any[]): RankingEntry[] =>
  employees.map(emp => ({
    rank: emp.rank || 0,
    previousRank: undefined,
    rankChange: 0,
    id: emp.empID,
    name: emp.employeeName || '',
    type: 'employee' as const,
    parentName: emp.email || '',
    attendancePercent: parseFloat(emp.presentInPercentage) || emp.currentPercent || 0,
    trendDirection: emp.trendDirection || (emp.trend > 1 ? 'up' : emp.trend < -1 ? 'down' : 'stable'),
    trendPercent: emp.trend || 0,
  }));

/** Generate sparkline values from a time series */
export const generateSparklineFromTimeSeries = (
  ts?: TimeSeriesPoint[],
  days = 14,
  field: 'overallPercent' | 'regularPercent' | 'contractualPercent' = 'overallPercent',
): number[] => {
  if (ts && ts.length > 0) {
    const recent = ts.slice(-days).map(p => p[field] ?? p.overallPercent ?? 0);
    if (recent.length < days) {
      const padVal = recent[0] ?? 0;
      return Array.from({ length: days - recent.length }, () => padVal).concat(recent);
    }
    return recent;
  }
  return Array.from({ length: days }, () => 0);
};

/** orderBy field for KV queries */
export const getOrderByField = (mode: 'regular' | 'contractual'): string =>
  mode === 'contractual' ? 'presentPercentageCon' : 'presentPercentageReg';

/** Transform API KV data to RankingEntry format */
export const transformKVDataToRankings = (
  data: any[],
  startRank = 1,
  mode: 'regular' | 'contractual' = 'regular',
): RankingEntry[] =>
  data.map((kv, index) => {
    let presentPercentReg = parseFloat(kv.presentPercentageReg) || 0;
    let presentPercentCon = parseFloat(kv.presentPercentageCon) || 0;
    if (presentPercentReg > 100) presentPercentReg /= 100;
    if (presentPercentCon > 100) presentPercentCon /= 100;
    const displayPercent = mode === 'contractual' ? presentPercentCon : presentPercentReg;

    return {
      rank: startRank + index,
      rankChange: 0,
      id: kv.kvID || kv.schoolID,
      name: kv.kvSchoolNameMaster || kv.kvName || kv.schoolName || '',
      type: 'kv' as const,
      parentName: kv.kvRoNameMaster || kv.roName || '',
      attendancePercent: Math.round(displayPercent * 10) / 10,
      trendDirection: displayPercent >= 85 ? ('up' as const) : displayPercent >= 75 ? ('stable' as const) : ('down' as const),
      trendPercent: 0,
      regularPercent: Math.round(presentPercentReg * 10) / 10,
      contractualPercent: Math.round(presentPercentCon * 10) / 10,
    } as RankingEntry;
  });

/** Generic helper to transform topPerformingSummary grouped-by-date data into RegionTrendData[] */
export const transformSummaryToRegionTrends = (
  rawData: any,
  employeeApiType: 'regular' | 'contractual',
): RegionTrendData[] => {
  if (!rawData) return [];
  const dateGroups = rawData;
  const roMap = new Map<number, { roId: number; roName: string; records: any[] }>();

  Object.keys(dateGroups).forEach(dateKey => {
    dateGroups[dateKey].forEach((record: any) => {
      const roId = record.roID || record.roId;
      if (!roMap.has(roId)) roMap.set(roId, { roId, roName: record.roName, records: [] });
      roMap.get(roId)!.records.push(record);
    });
  });

  const trends: RegionTrendData[] = Array.from(roMap.values()).map((ro, index) => {
    const sorted = ro.records.sort(
      (a: any, b: any) => new Date(a.attendanceDate).getTime() - new Date(b.attendanceDate).getTime(),
    );

    const timeSeries: TimeSeriesPoint[] = sorted.map((r: any) => {
      const regTotal = parseInt(r.totalEnrollmentReg) || 0;
      const conTotal = parseInt(r.totalEnrollmentCon) || 0;
      const regPercent = parseFloat(r.presentPercentageReg) || 0;
      const conPercent = parseFloat(r.presentPercentageCon) || 0;
      const regPresent = Math.round((regTotal * regPercent) / 100);
      const conPresent = Math.round((conTotal * conPercent) / 100);
      const overallPercent =
        regTotal + conTotal > 0 ? ((regPresent + conPresent) / (regTotal + conTotal)) * 100 : 0;

      return {
        date: new Date(r.attendanceDate).toISOString().split('T')[0],
        timestamp: new Date(r.attendanceDate).getTime(),
        regularPresent: regPresent,
        contractualPresent: conPresent,
        totalPresent: regPresent + conPresent,
        regularTotal: regTotal,
        contractualTotal: conTotal,
        totalStaff: regTotal + conTotal,
        regularPercent: regPercent,
        contractualPercent: conPercent,
        overallPercent,
      };
    });

    const avgReg = timeSeries.length > 0 ? timeSeries.reduce((s, p) => s + p.regularPercent, 0) / timeSeries.length : 0;
    const avgCon = timeSeries.length > 0 ? timeSeries.reduce((s, p) => s + p.contractualPercent, 0) / timeSeries.length : 0;

    const mid = Math.floor(timeSeries.length / 2);
    const pickField = (p: TimeSeriesPoint) => (employeeApiType === 'contractual' ? p.contractualPercent : p.regularPercent);
    const firstAvg = mid > 0 ? timeSeries.slice(0, mid).reduce((s, p) => s + pickField(p), 0) / mid : 0;
    const secondAvg =
      timeSeries.length - mid > 0
        ? timeSeries.slice(mid).reduce((s, p) => s + pickField(p), 0) / (timeSeries.length - mid)
        : 0;

    const trendPercent = Math.round((secondAvg - firstAvg) * 10) / 10;
    const trendDirection: 'up' | 'down' | 'stable' = trendPercent > 1 ? 'up' : trendPercent < -1 ? 'down' : 'stable';

    return {
      roId: ro.roId,
      roName: ro.roName,
      color: REGION_COLORS[index % REGION_COLORS.length],
      timeSeries,
      averageAttendance: Math.round((employeeApiType === 'contractual' ? avgCon : avgReg) * 10) / 10,
      trendDirection,
      trendPercent,
      rank: index + 1,
    };
  });

  return trends;
};

// ─── Computed date range type ────────────────────────────────
export interface ComputedDateRange {
  fromDateStr: string;
  toDateStr: string;
  prevFromDateStr: string;
  prevToDateStr: string;
  isValid: boolean;
}

/** Compute the date range + previous period for trend comparison */
export const useComputedDateRange = (
  dateRange: number,
  isCustomDateRange: boolean,
  customFromDate: string,
  customToDate: string,
): ComputedDateRange =>
  useMemo(() => {
    if (isCustomDateRange && customFromDate && customToDate) {
      const from = new Date(customFromDate);
      const to = new Date(customToDate);
      const diff = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
      const prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      const prevFrom = new Date(prevTo);
      prevFrom.setDate(prevFrom.getDate() - diff);

      return {
        fromDateStr: customFromDate,
        toDateStr: customToDate,
        prevFromDateStr: prevFrom.toISOString().split('T')[0],
        prevToDateStr: prevTo.toISOString().split('T')[0],
        isValid: true,
      };
    }

    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - dateRange);
    const prevTo = new Date(from);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - dateRange);

    return {
      fromDateStr: from.toISOString().split('T')[0],
      toDateStr: to.toISOString().split('T')[0],
      prevFromDateStr: prevFrom.toISOString().split('T')[0],
      prevToDateStr: prevTo.toISOString().split('T')[0],
      isValid: true,
    };
  }, [dateRange, isCustomDateRange, customFromDate, customToDate]);

// ─── Tab type ────────────────────────────────────────────────
export type TabType = 'overview' | 'regions' | 'schools' | 'employees';

// ─── Shared dashboard data returned by the main hook ─────────
export interface AnalyticsDashboardData {
  // Date range
  computedDateRange: ComputedDateRange;
  // Core RTK data (always fetched)
  totalEmpData: any;
  presentData: any;
  absentData: any;
  attendancePercData: any;
  dailyAttendanceSummary: any;
  totalKVISData: any;
  // RO summary (heatmap + top/bottom regions)
  processedROData: { heatmap: any[]; topRegions: RankingEntry[]; bottomRegions: RankingEntry[] };
  // Region trends for overview chart
  topRegionTrends: RegionTrendData[];
  // Regions from KV data (for filters)
  regions: any[];
  // Loading flags
  isCoreLoading: boolean;
  // Employee type toggle
  employeeApiType: 'regular' | 'contractual';
}

/**
 * Master data hook used by the shell + overview.
 * Only fetches data that's needed regardless of active tab.
 */
export function useAnalyticsCoreData(
  dateRange: number,
  isCustomDateRange: boolean,
  customFromDate: string,
  customToDate: string,
  employeeApiType: 'regular' | 'contractual',
) {
  const computedDR = useComputedDateRange(dateRange, isCustomDateRange, customFromDate, customToDate);

  // ── Core RTK queries (always needed) ─────────────────
  // Using skip/cache instead of refetchOnMountOrArgChange to reduce DB connections
  const { data: totalEmpData, isLoading: l1 } = useGetTotalEmployeesQuery(undefined, {
    // Rely on RTK Query cache (keepUnusedDataFor: 300s default)
  });
  const { data: presentData, isLoading: l2 } = useGetStaffPresentTodayQuery(undefined, {
    // Rely on RTK Query cache
  });
  const { data: absentData, isLoading: l3 } = useGetStaffAbsentTodayQuery(undefined, {
    // Rely on RTK Query cache  
  });
  const { data: attendancePercData, isLoading: l4 } = useGetAttendancePercDateRangeQuery(
    { fromDate: computedDR.fromDateStr, toDate: computedDR.toDateStr },
    { skip: !computedDR.isValid },
  );
  const { data: dailyAttendanceSummary, isLoading: l5 } = useGetDailyAttendanceSummaryQuery(
    { fromDate: computedDR.fromDateStr, toDate: computedDR.toDateStr },
    { skip: !computedDR.isValid },
  );
  const { data: totalKVISData } = useGetTotalKVISQuery(undefined, {
    // School count rarely changes - use default cache
  });

  // ── RO Summary (heatmap + top/bottom) ─────────────────
  const { data: roSummaryData, isLoading: l6 } = useGetROSummaryQuery(
    { fromDate: computedDR.fromDateStr, toDate: computedDR.toDateStr },
    { skip: !computedDR.isValid },
  );
  const { data: prevROSummaryData } = useGetROSummaryQuery(
    { fromDate: computedDR.prevFromDateStr, toDate: computedDR.prevToDateStr },
    { skip: !computedDR.isValid },
  );

  // ── Top 5 region trends (overview chart) ──────────────
  const { data: topRegionSummaryData } = useGetTopPerformingSummaryQuery(
    { fromDate: computedDR.fromDateStr, toDate: computedDR.toDateStr, flag: 'ro', limit: 5, order: 'DESC' },
    { skip: !computedDR.isValid },
  );

  // ── Regions from KV no-paging (replaces manual attendanceApi call) ──
  const { data: kvNoPagingData } = useGetDailyAttendanceByKVSNoPagingQuery(undefined, {
    // Rely on RTK Query cache
  });

  // ── Derived: regions list from KV data ────────────────
  const regions = useMemo(() => {
    const kvData = kvNoPagingData?.data || [];
    const unique: any[] = [];
    const seen = new Set<string>();
    kvData.forEach((kv: any) => {
      const roName = kv.roName || kv.kvRoNameMaster || '';
      if (!seen.has(roName)) {
        seen.add(roName);
        unique.push({ roId: kv.roId || kv.kvRoId || 0, roName });
      }
    });
    return unique;
  }, [kvNoPagingData]);

  // ── Derived: processedROData ──────────────────────────
  const processedROData = useMemo(() => {
    if (!roSummaryData?.data) return { heatmap: [], topRegions: [], bottomRegions: [] };

    const prevMap = new Map<number, any>();
    prevROSummaryData?.data?.forEach((ro: any) => prevMap.set(ro.roID, ro));

    const rankings: RankingEntry[] = roSummaryData.data.map((ro: any) => {
      const regPercent = parseFloat(ro.presentPercentageReg) || 0;
      const conPercent = parseFloat(ro.presentPercentageCon) || 0;
      const regTotal = parseInt(ro.totalEnrollmentReg) || 0;
      const conTotal = parseInt(ro.totalEnrollmentCon) || 0;

      const displayPercent =
        employeeApiType === 'contractual'
          ? conPercent
          : employeeApiType === 'regular'
            ? regPercent
            : regTotal + conTotal > 0
              ? (regTotal * regPercent + conTotal * conPercent) / (regTotal + conTotal)
              : 0;

      let trendPercent = 0;
      let trendDirection: 'up' | 'down' | 'stable' = 'stable';
      const prev = prevMap.get(ro.roID);
      if (prev) {
        const prevReg = parseFloat(prev.presentPercentageReg) || 0;
        const prevCon = parseFloat(prev.presentPercentageCon) || 0;
        const prevRegT = parseInt(prev.totalEnrollmentReg) || 0;
        const prevConT = parseInt(prev.totalEnrollmentCon) || 0;
        const prevDisplay =
          employeeApiType === 'contractual'
            ? prevCon
            : employeeApiType === 'regular'
              ? prevReg
              : prevRegT + prevConT > 0
                ? (prevRegT * prevReg + prevConT * prevCon) / (prevRegT + prevConT)
                : 0;
        trendPercent = Math.round((displayPercent - prevDisplay) * 10) / 10;
        trendDirection = trendPercent > 0.5 ? 'up' : trendPercent < -0.5 ? 'down' : 'stable';
      } else {
        trendDirection = displayPercent >= 85 ? 'up' : displayPercent >= 75 ? 'stable' : 'down';
      }

      return {
        rank: 0,
        rankChange: 0,
        id: ro.roID,
        name: ro.roName,
        type: 'region' as const,
        attendancePercent: Math.round(displayPercent * 10) / 10,
        trendDirection,
        trendPercent,
      };
    });

    rankings.sort((a, b) => b.attendancePercent - a.attendancePercent);
    rankings.forEach((r, i) => (r.rank = i + 1));

    const heatmap = roSummaryData.data.map((ro: any) => {
      const regTotal = Number(ro.totalEnrollmentReg) || 0;
      const conTotal = Number(ro.totalEnrollmentCon) || 0;
      const regPercent = Number(ro.presentPercentageReg) || 0;
      const conPercent = Number(ro.presentPercentageCon) || 0;
      const overall =
        regTotal + conTotal > 0
          ? (regTotal * regPercent + conTotal * conPercent) / (regTotal + conTotal)
          : 0;

      return {
        stateName: ro.roName,
        value: Number(overall.toFixed(1)),
        regularPercent: Number(regPercent.toFixed(1)),
        contractualPercent: Number(conPercent.toFixed(1)),
        staffCount: regTotal + conTotal,
        totalSchools: Number(ro.totalSchools) || 0,
      };
    });

    return {
      heatmap,
      topRegions: rankings.slice(0, 5),
      bottomRegions: rankings.slice(-5).reverse(),
    };
  }, [roSummaryData, prevROSummaryData, employeeApiType]);

  // ── Derived: top region trends (overview chart) ───────
  const topRegionTrends = useMemo(
    () => transformSummaryToRegionTrends(topRegionSummaryData?.data, employeeApiType),
    [topRegionSummaryData, employeeApiType],
  );

  // ── Derived: dashboard metrics ────────────────────────
  const metrics = useMemo(() => {
    const totalEmployees = totalEmpData?.totalEmployee || 0;
    const percData = attendancePercData?.data;

    let regularAttendance: number;
    let contractualAttendance: number;
    let overallAttendance: number;

    if (percData) {
      regularAttendance = parseFloat(percData.avgPresentPercentageReg) || 0;
      contractualAttendance = parseFloat(percData.avgPresentPercentageCon) || 0;
      const totalEnrollment =
        parseInt(percData.sumOfTotalEnrollmentReg || '0') + parseInt(percData.sumOfTotalEnrollmentCon || '0');
      const totalPresent =
        parseFloat(percData.sumOfTotalPresentReg || '0') + parseFloat(percData.sumOfTotalPresentCon || '0');
      overallAttendance = totalEnrollment > 0 ? (totalPresent / totalEnrollment) * 100 : 0;
    } else {
      const totalPresent = presentData?.totalPresentToday || 0;
      overallAttendance = totalEmployees > 0 ? (totalPresent / totalEmployees) * 100 : 0;
      const regTotal = totalEmpData?.totalRegisteredEmp || 0;
      const regPresent = presentData?.totalPresentRegToday || 0;
      const conTotal = totalEmpData?.totalContractEmpCount || 0;
      const conPresent = presentData?.totalContractualPresentToday || 0;
      regularAttendance = regTotal > 0 ? (regPresent / regTotal) * 100 : 0;
      contractualAttendance = conTotal > 0 ? (conPresent / conTotal) * 100 : 0;
    }

    return {
      totalEmployees,
      overallAttendance: Math.round(overallAttendance * 10) / 10,
      regularAttendance: Math.round(regularAttendance * 10) / 10,
      contractualAttendance: Math.round(contractualAttendance * 10) / 10,
    };
  }, [totalEmpData, presentData, attendancePercData]);

  const isCoreLoading = l1 || l2 || l3 || l4 || l5 || l6;

  return {
    computedDateRange: computedDR,
    totalEmpData,
    presentData,
    absentData,
    attendancePercData,
    dailyAttendanceSummary,
    totalKVISData,
    processedROData,
    topRegionTrends,
    regions,
    metrics,
    isCoreLoading,
  };
}
